import React from 'react';
import UserPlaceholder from 'assets/svg/profile-new.svg';
import PhoneIcon from 'assets/svg/phone-new.svg';
import SendIcon from 'assets/svg/send.svg';
import DoneIcon from 'assets/svg/done.svg';
import * as Styled from './styled';
import { useLocale } from 'locale';
import { PrivateContactProps } from 'components/contact/types';
import { PoiId } from 'utils/entities';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { TextField } from 'ds/components/input/TextField';
import { Poi } from 'utils/marketplaceRoutes';
import { createMortgageValidationFn } from 'components/madad/contact-modal/utils';
import { ContactAgentModalSource } from 'store/state/app';
import { SubmitContactAgentPayload, ContactAgentSubmitterSource } from 'store/state/app/payloads';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { Text } from 'ds/components/typography';
import { noop } from 'lodash';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { SpacingScale } from 'ds/theme/spacing';
import TermsLine from 'components/terms-line';
import _ from 'lodash';

interface SideBarPrivateContactProps extends PrivateContactProps {
  id: PoiId;
  source: ContactAgentModalSource;
  sidePaddingSpacing?: SpacingScale;
  onContactAgentInputClick: (eventName: string, poi: Poi) => void;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
  handleSubmitEvent?: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

interface State {
  userName: string;
  userPhone: string;
  formErrors: {
    phone?: string;
    message?: string;
    name?: string;
  };
}

export const SideBarPrivateContact: React.FC<SideBarPrivateContactProps> = (props) => {
  const {
    id,
    poi,
    name,
    phone,
    source,
    poiType,
    isContacted,
    contactValues,
    onCallClickEvent = noop,
    handleSubmitEvent = noop,
    onContactPageSubmit,
    onContactAgentInputClick,
    sidePaddingSpacing,
  } = props;

  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;
  const [ phoneNumberIsVisible, setPhoneNumberIsVisible ] = React.useState(false);
  const [ state, setState ] = React.useState<State>({ userName: contactValues.name, userPhone: contactValues.phone, formErrors: {} });

  const handlePhoneSubmit = (e: React.MouseEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || phoneNumberIsVisible, { phone: state.userPhone, name: state.userName, isMortgageConsult: false, id, type: poiType }, source);

    if (!phoneNumberIsVisible && !isMobile) {
      e.preventDefault();
      setPhoneNumberIsVisible(true);
    }
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userPhone: e.currentTarget.value });
  const onMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userName: e.currentTarget.value });


  const validate = React.useCallback(createMortgageValidationFn({
    phone: {
      invalid: t('contactAgentModal.form.phone.invalid'),
      required: t('contactAgentModal.form.phone.required'),
    },
    message: {
      required: t('contactAgentModal.form.message.required'),
    },
    name: {
      invalid: t('contactAgentModal.form.name.invalid'),
      tooLong: t('contactAgentModal.form.name.tooLong'),
    },
  }, contactValues.name), [ t, contactValues.name ]);

  const onSubmit = () => {
    const errors = validate({
      phone: state.userPhone,
      name: state.userName,
    });
    const formValues = {
      phone: state.userPhone,
      name: state.userName,
      isMortgageConsult: false,
      type: poiType,
      id,
      source,
    };
    setState({ ...state, formErrors: errors });

    let hasErrors = false;

    if (errors.phone || errors.name) {
      hasErrors = true;
    }

    handleSubmitEvent(hasErrors, formValues, source);

    if (hasErrors) return;

    onContactPageSubmit({
      key: formValues.id,
      variables: formValues,
      source: ContactAgentSubmitterSource.Form,
    });
  };

  if (isContacted) {
    return (
      <Styled.Root>
        <DoneIcon width={62} height={62} />
        <Text>{t('contactForm.contact.form.withMortgage.succeedMessage.modeB')}</Text>
      </Styled.Root>
    );
  }

  return (
    <Styled.Root sidePaddingSpacing={sidePaddingSpacing}>
      <Styled.FormTitle weight="bold">{t('contactForm.contact.form.withMortgage.mobile.title', { isContacted: false })}</Styled.FormTitle>
      <Styled.InfoBox>
        <UserPlaceholder width={80} height={80} />
        <Styled.UserName weight="bold">{name}</Styled.UserName>
      </Styled.InfoBox>
      <div style={{ width: '100%' }}>
        <Styled.PhoneButton
          data-auto="phone-number-button"
          size="extraLarge"
          fullWidth
          weight="medium"
          onClick={handlePhoneSubmit}
          href={createPhoneURI(phone)}
        >
          <PhoneIcon />
          {phoneNumberIsVisible ? phone : (isMobile ? t('contactAgentModal.callButton') : t('contactAgentModal.callButton.desktop'))}
        </Styled.PhoneButton>
      </div>

      <Styled.HintTitle weight="medium">
        {t('contactForm.contact.sidebar.private.hint')}
      </Styled.HintTitle>
      <Styled.FormBody>
        <TextField
          fullWidth
          data-auto="name-input"
          hasError={Boolean(state.formErrors.name)}
          errorMessage={state.formErrors.name}
          value={state.userName}
          onChange={onMessageChange}
          placeholder={t('contactForm.contact.name')}
          onClick={() => onContactAgentInputClick('property_lead_form_messege_box_click', poi)}
        />
        <TextField
          fullWidth
          value={state.userPhone}
          hasError={Boolean(state.formErrors.phone)}
          data-auto="phone-input"
          errorMessage={state.formErrors.phone}
          onChange={onPhoneChange}
          placeholder={t('contactForm.contact.form.placeholder.phone')}
          onClick={() => onContactAgentInputClick('property_lead_form_phone_text_box_click', poi)}
        />
        <Styled.SubmitButton onClick={onSubmit} data-auto="submit-lead-button">
          <SendIcon />
        </Styled.SubmitButton>
      </Styled.FormBody>
      <TermsLine />
    </Styled.Root>
  );
};
